/**
 * useGenerationJob — shared derived-state for tab generation hooks.
 *
 * useMusicGeneration / useImageGeneration / useSfxGeneration all derived the
 * SAME activeJob/resultJob/isGenerating/progress/startedAt/result/error state
 * from GenerationContext. This hook centralizes that derivation so each feature
 * hook keeps only its own config + submit logic.
 *
 * Progress modes:
 *  - `expectedSeconds` set  → progress is smoothed via useEstimatedProgress and
 *    snaps to 100 on completion; `startedAt` tracks the run's wall-clock start.
 *  - `expectedSeconds` omitted → progress is the raw backend value (0-100); the
 *    estimator stays idle (used by sound effects, which gets real progress).
 */

import { useState, useCallback, useEffect } from 'react';
import { useGeneration } from '../contexts/GenerationContext.context';
import type { GenerationJob, JobError, GenerationTabMode } from '../types/generationJob';
import { useEstimatedProgress } from './useEstimatedProgress';

/**
 * The error to show for a tab, given its latest active and finished jobs.
 *
 * A finished FAILED job stays in `results` until a newer job displaces it — and
 * `results` is persisted to localStorage, so it also survives a reload. Reading
 * it unconditionally meant the previous run's failure kept rendering while the
 * NEXT run was in flight: the user pressed Generate on a different model, saw
 * the spinner start, and the old red "Generation failed: …" banner stayed put
 * until the new job finished (or forever, if they never ran another one).
 *
 * So a run that is currently in flight suppresses the previous run's error. The
 * banner comes back — with the new message — if this run fails too.
 *
 * Shared by every tab hook (useGenerationJob and the hand-rolled ones) so the
 * rule is defined once instead of re-derived per tab.
 */
export function selectJobError(
  activeJob: GenerationJob | undefined,
  resultJob: GenerationJob | undefined,
  isGenerating: boolean
): JobError | null {
  if (isGenerating) return null;
  if (activeJob?.error) return activeJob.error;
  if (resultJob?.status === 'failed') return resultJob.error ?? null;
  return null;
}

/**
 * Re-read a tab's finished job and resolve its result with freshly signed links.
 *
 * A finished job's media links are presigned and expire (the worker signs them
 * for an hour) while the result stays on screen for a day. The backend re-signs
 * them on every read of the job, so anything about to hand one to the network
 * -- a player that errored, a download -- asks for this first, and the fresh
 * result replaces the stored one. Rejects when there is no finished job or it
 * can no longer be read: the caller says so instead of using the dead link.
 *
 * Shared by every tab hook (useGenerationJob and the hand-rolled ones) so the
 * refresh is written once.
 */
export function useRefreshResult<T>(resultJob: GenerationJob | undefined): () => Promise<T> {
  const { refreshResult } = useGeneration();
  const jobId = resultJob?.status === 'completed' ? resultJob.id : undefined;
  return useCallback(async (): Promise<T> => {
    if (!jobId) throw new Error('No finished job to refresh');
    return (await refreshResult(jobId)) as T;
  }, [jobId, refreshResult]);
}

export interface UseGenerationJobOptions {
  /**
   * Smooth `progress` over this many seconds (and track `startedAt`). Omit to
   * report raw backend progress with no wall-clock tracking.
   */
  expectedSeconds?: number;
  /**
   * OR'd into `isGenerating` — for a synchronous start window that precedes the
   * backend job id (e.g. image generation's blocking POST). Default: false.
   */
  extraActive?: boolean;
}

export interface UseGenerationJobResult<T> {
  /** Latest active (queued/running) job for the tab, if any. */
  activeJob: GenerationJob | undefined;
  /** Latest finished job for the tab, if any. */
  resultJob: GenerationJob | undefined;
  /** True while a job is queued/running (or while `extraActive`). */
  isGenerating: boolean;
  /** 0-100. Smoothed when `expectedSeconds` is set, else raw backend progress. */
  progress: number;
  /** ISO timestamp of the current run's start (rising edge of isGenerating), else null. */
  startedAt: string | null;
  /** Typed result of the latest COMPLETED job, else null. */
  result: T | null;
  /** Id of the latest finished job, else null. */
  currentJobId: string | null;
  /** Error message from the active/failed job (does NOT include submit errors). */
  jobError: string | null;
  /** Cancel + clear the active job, clear the result job, reset startedAt. */
  clearJobs: () => void;
  /** Re-read the finished job for freshly signed links (see useRefreshResult). */
  refreshResult: () => Promise<T>;
}

export function useGenerationJob<T>(
  tabMode: GenerationTabMode,
  options: UseGenerationJobOptions = {}
): UseGenerationJobResult<T> {
  const { expectedSeconds, extraActive = false } = options;
  const { failJob, clearJob, clearResults, getLatestActiveByTab, getLatestResultByTab } =
    useGeneration();

  const activeJob = getLatestActiveByTab(tabMode);
  const resultJob = getLatestResultByTab(tabMode);
  const current = activeJob ?? resultJob;

  const isGenerating =
    extraActive ||
    (!!activeJob && (activeJob.status === 'queued' || activeJob.status === 'running'));

  const backendProgress = current?.progress ?? null;

  const estimatedProgress = useEstimatedProgress({
    isActive: isGenerating && expectedSeconds !== undefined,
    expectedSeconds: expectedSeconds ?? 0,
    backendProgress,
  });

  let progress: number;
  if (expectedSeconds === undefined) {
    progress = current?.progress ?? 0;
  } else if (isGenerating) {
    progress = estimatedProgress;
  } else if (current?.status === 'completed') {
    progress = 100;
  } else {
    progress = backendProgress ?? 0;
  }

  // Track the wall-clock start of the current run so ElapsedTimeDisplay can
  // render. Set on the rising edge of isGenerating, cleared when fully idle.
  const [startedAt, setStartedAt] = useState<string | null>(null);
  useEffect(() => {
    if (isGenerating && startedAt === null) {
      setStartedAt(new Date().toISOString());
    } else if (!isGenerating && !current) {
      setStartedAt(null);
    }
  }, [isGenerating, startedAt, current]);

  const completedResult =
    resultJob?.status === 'completed' ? (resultJob.result as T | undefined) : undefined;

  const result = completedResult ?? null;

  const jobError = selectJobError(activeJob, resultJob, isGenerating)?.message ?? null;

  const currentJobId = resultJob?.id ?? null;

  const clearJobs = useCallback((): void => {
    setStartedAt(null);
    if (activeJob) {
      failJob(activeJob.id, { message: 'Cancelled by user', code: 'CANCELLED' }, 'cancelled');
      clearJob(activeJob.id);
    }
    // Every finished job of the tab, not just the latest: results stack per tab,
    // so clearing only the newest put the one before it back on screen -- a
    // failed run resurfaced as 'Generation Failed' after a successful one.
    clearResults(tabMode);
  }, [activeJob, failJob, clearJob, clearResults, tabMode]);

  const refreshResult = useRefreshResult<T>(resultJob);

  return {
    activeJob,
    resultJob,
    isGenerating,
    progress,
    startedAt,
    result,
    currentJobId,
    jobError,
    clearJobs,
    refreshResult,
  };
}

export default useGenerationJob;
